import {FormikErrors} from 'formik';
import get from 'lodash/get';
import {set} from 'lodash';
import type {newSubplanFields} from './builderSetUpNewFields';
import {OVRecommendActionEnum} from '../../../schema-types/globalTypes';
import {builderFieldsType} from '../CreateBuilder/builderFields';
import {hasWrapper} from '../CreateBuilder/BuilderSourceDestination/builderUtils';
import {
  validateTextField,
  validateMandateField,
} from '../CreateBuilder/BuilderSourceDestination/builderValidators';
import {planFeesFieldType} from '../CreateBuilder/BuilderPlanFees';
import {isBondPlan} from './bondSetUpNew';

export const setUpNewValidator = (
  values: builderFieldsType,
  errors: FormikErrors<builderFieldsType>
): FormikErrors<builderFieldsType> => {
  if (!values.goal?.id) {
    if (!values.isSppDa) {
      errors.goal = 'Please select a goal';
    }
  }
  const setUpNewValues = values.setUpNew;
  if (!setUpNewValues) {
    errors.setUpNew = 'Fill in fields';
    return errors;
  }

  newPlanValidator({
    values,
    errors,
    formikKeyPrefix: `setUpNew`,
  });
  return errors;
};

type subplanValidatorArgs = {
  values: builderFieldsType;
  errors: FormikErrors<builderFieldsType>;
  formikKeyPrefix: string;
  context:
    | OVRecommendActionEnum.CONTRIBUTE
    | OVRecommendActionEnum.TRANSFER
    | OVRecommendActionEnum.SET_UP_NEW;
};
export const newSubplanValidator = ({
  errors,
  values,
  formikKeyPrefix,
  context,
}: subplanValidatorArgs): void => {
  validateTextField(
    get(values, `${formikKeyPrefix}.planProvider`),
    `${formikKeyPrefix}.planProvider`,
    errors
  );

  validateMandateField(
    get(values, `${formikKeyPrefix}.model`),
    `${formikKeyPrefix}.model`,
    errors
  );

  if (values.isSpp) {
    validateSppFpFeePercentage(
      get(values, `${formikKeyPrefix}.planFees.sppFpFeePercentage`),
      `${formikKeyPrefix}.planFees.sppFpFeePercentage`,
      errors
    );
  }
};

// SPP plans capture an ongoing FP fee percentage (e.g. 0.15 == 0.15%).
// Mirrors the backend validation in api/recommendation_builder/set_up_new.py:
// required, between 0.00 and 1.00, at most 2 decimal places.
export const validateSppFpFeePercentage = (
  value: unknown,
  key: string,
  errors: FormikErrors<builderFieldsType>
): void => {
  if (value === null || value === undefined || value === '') {
    set(errors, key, 'Required');
    return;
  }
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) {
    set(errors, key, 'Must be a number');
    return;
  }
  if (numericValue < 0 || numericValue > 1) {
    set(errors, key, 'Must be between 0.00 and 1.00');
    return;
  }
  const [, decimals] = String(value).split('.');
  if (decimals && decimals.length > 2) {
    set(errors, key, 'Must have at most 2 decimal places');
  }
};

export const planFeeValidator = ({
  values,
  formikKeyPrefix,
  errors,
}: {
  values: builderFieldsType;
  errors: FormikErrors<builderFieldsType>;
  formikKeyPrefix: string;
}): void => {
  const planFees = get(
    values,
    `${formikKeyPrefix}.planFees`
  ) as unknown as planFeesFieldType | null;
  // Ongoing fee IDs are composed by the backend and no longer selected client-side,
  // so the only thing to validate is that the planFees object is present at all.
  if (!planFees) {
    set(errors, `${formikKeyPrefix}.planFees`, 'Please set plan fees');
  }
};

type newPlanValidatorArgs = {
  values: builderFieldsType;
  errors: FormikErrors<builderFieldsType>;
  formikKeyPrefix: string;
  // context:
  //   | OVRecommendActionEnum.CONTRIBUTE
  //   | OVRecommendActionEnum.TRANSFER
  //   | OVRecommendActionEnum.SET_UP_NEW;
  isLowesPension?: boolean;
  isTransactBond?: boolean;
  isMinor?: boolean;
};
export const newPlanValidator = ({
  values,
  errors,
  formikKeyPrefix,
  // context,
  isLowesPension = false,
  isTransactBond = false,
  isMinor = false,
}: newPlanValidatorArgs): void => {
  validateTextField(
    get(values, `${formikKeyPrefix}.parent.productNameId`),
    `${formikKeyPrefix}.parent.productNameId`,
    errors
  );
  validateTextField(
    get(values, `${formikKeyPrefix}.parent.planProvider`),
    `${formikKeyPrefix}.parent.planProvider`,
    errors
  );
  if (isMinor && !get(values, `${formikKeyPrefix}.parent.parentGuardianId`)) {
    set(errors, `${formikKeyPrefix}.parent.parentGuardianId`, 'Required');
  }
  // TODO: check if everything has subplans now
  const subplans = get(values, `${formikKeyPrefix}.subplans`, []) as newSubplanFields[];

  if (!subplans.length) {
    set(errors, `${formikKeyPrefix}.subplans`, 'At least one subplan must be selected');
  }

  const isBond = isBondPlan(values.mainPlanType);
  const fpFeeKey = `${formikKeyPrefix}.parent.planFees.fpFeeId`;
  // SPP gets no Off Platform FP fee options, so there is nothing to pick.
  if (isBond && !values.isSpp && !get(values, fpFeeKey)) {
    set(errors, fpFeeKey, 'Select an FP fee');
  }
  if (isBond && subplans.length > 1) {
    set(errors, `${formikKeyPrefix}.subplans`, 'A bond must have exactly one subplan');
    return;
  }

  subplans.forEach((_, index) => {
    const subplanPrefix = `${formikKeyPrefix}.subplans.${index}`;
    newSubplanValidator({
      errors,
      formikKeyPrefix: subplanPrefix,
      values,
      context: OVRecommendActionEnum.SET_UP_NEW,
    });
    // A bond subplan is always Saltus, which only accepts a model portfolio.
    if (isBond && typeof get(values, `${subplanPrefix}.model`) === 'string') {
      set(errors, `${subplanPrefix}.model`, 'Select a Saltus model portfolio');
    }
  });
};
